import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  LogOut,
  Menu,
  X,
  ChevronRight,
  Heart,
  Droplets,
  Scale,
  Camera,
  Video,
  Trophy,
  Footprints,
  Package,
  User,
} from 'lucide-react';
import { PersonalizedHeader } from './PersonalizedHeader';
import { AddDeviceModal } from './AddDeviceModal';
import { GlucoseTrends } from './GlucoseTrends';
import { MultiDayGlucoseChart } from './MultiDayGlucoseChart';
import { LowBloodGlucoseIndex } from './LowBloodGlucoseIndex';
import { UserSettings } from './UserSettings';
import { useQuery } from '@tanstack/react-query';
import { subscriptionsService } from '../services/subscriptions.service';
import { communityService } from '../services/community.service';
import { useSugarReadings7Days } from '../hooks/useSugarReadings';
import { DashboardSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

interface DashboardProps {
  onNavigate: (page: string) => void;
  onQuickAdd?: () => void;
}

export function Dashboard({ onNavigate, onQuickAdd }: DashboardProps) {
  const getStoredTipPopupDisabled = () =>
    window.localStorage.getItem('dropsugar:dashboard-tip-disabled') === 'true';
  const getLastSeenTip = () => window.localStorage.getItem('dropsugar:dashboard-tip-last-seen') || '';

  const { user, signOut } = useAuth();
  const { showQuickActions } = useFeatureFlags();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showTipPopup, setShowTipPopup] = useState(false);
  const [tipPopupDisabled, setTipPopupDisabled] = useState(getStoredTipPopupDisabled);

  const subscriptionResult = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      try {
        return await subscriptionsService.getCurrentSubscription();
      } catch (e) {
        console.warn('Subscription fetch failed:', e);
        return null;
      }
    },
    enabled: !!user,
    retry: false,
  });
  const subscription = subscriptionResult?.data ?? null;
  const subscriptionLoading = subscriptionResult?.isLoading ?? false;
  const subscriptionError = subscriptionResult?.error ?? null;

  const dailyTipResult = useQuery({
    queryKey: ['daily-tip'],
    queryFn: async () => {
      try {
        return await communityService.getDailyTip();
      } catch {
        return null;
      }
    },
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
  });
  const dailyTipData = dailyTipResult?.data ?? null;
  const tipLoading = dailyTipResult?.isLoading ?? false;
  const dailyTip = dailyTipData?.tipText || '';

  const sugarReadingsResult = useSugarReadings7Days();
  const isDummyData = sugarReadingsResult?.isDummyData === true;

  const dummyTip = 'Monitor your blood glucose regularly and aim for time-in-range of 70-180 mg/dL. Stay hydrated and include fiber-rich foods in your meals.';
  const displayTip = (isDummyData && !dailyTip) ? dummyTip : dailyTip;

  const sub = subscription as { isExpired?: boolean; isTrial?: boolean; trialExpiresAt?: string; expiresAt?: string; package?: { name?: string } } | null;
  const isExpired = !!(sub?.isExpired);

  useEffect(() => {
    if (subscriptionError) {
      console.error('Subscription error:', subscriptionError);
    }
  }, [subscriptionError]);

  useEffect(() => {
    if (!displayTip || tipPopupDisabled) {
      setShowTipPopup(false);
      return;
    }
    if (getLastSeenTip() === displayTip) {
      setShowTipPopup(false);
      return;
    }
    setShowTipPopup(true);
  }, [displayTip, tipPopupDisabled]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error(handleApiError(error));
    }
  };

  const isLoading = subscriptionLoading || tipLoading;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const expiryDate = sub?.trialExpiresAt || sub?.expiresAt;
  const packageName = sub?.package?.name;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Forced renewal modal when subscription expired - blocks usage until user renews */}
      {isExpired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border-2 border-red-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Subscription Expired</h2>
                <p className="text-gray-600 text-sm">Please renew to continue using DropSugar</p>
              </div>
            </div>

            {/* Expired package information */}
            <div className="bg-gray-50 rounded-2xl p-5 mb-6 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Your expired subscription</h3>
              <div className="space-y-2">
                {packageName && (
                  <p className="text-gray-900">
                    <span className="font-medium">Package:</span> {packageName}
                  </p>
                )}
                {expiryDate && (
                  <p className="text-gray-900">
                    <span className="font-medium">Expired on:</span>{' '}
                    {new Date(expiryDate).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                )}
                {sub?.isTrial && (
                  <p className="text-sm text-amber-700 font-medium">Free Trial</p>
                )}
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Select a package below to renew your subscription and regain full access.
            </p>

            <button
              onClick={() => onNavigate('packages')}
              className="w-full py-3 px-6 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-xl transition-colors"
            >
              Renew Subscription
            </button>
          </div>
        </div>
      )}
      {/* Subscription expiry warning banner - when expires within 7 days */}
      {!isExpired && subscription && (() => {
        const exp = (sub as any)?.trialExpiresAt || (sub as any)?.expiresAt;
        if (!exp) return null;
        const expiry = new Date(exp);
        const now = new Date();
        const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        if (daysLeft <= 0 || daysLeft > 7) return null;
        return (
          <div className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-amber-900 font-medium">
                Your subscription {daysLeft === 1 ? 'expires tomorrow' : `expires in ${daysLeft} days`}.
                Renew now to avoid interruption.
              </p>
            </div>
            <button
              onClick={() => onNavigate('packages')}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors"
            >
              Renew
            </button>
          </div>
        );
      })()}

      {/* Dashboard-specific nav - not shared AppTopNav */}
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/dropsugar-logo.jpg"
                alt="DropSugar"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-dropsugar-blue to-dropsugar-green bg-clip-text text-transparent">
                  DropSugar
                </h1>
                <p className="text-xs text-gray-600">{user?.email}</p>
              </div>
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="hidden md:flex items-center space-x-2">
              <button
                onClick={() => onNavigate('manage-package')}
                className="p-2 text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10 rounded-lg transition-all"
                title="Manage Package"
              >
                <Package className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 text-gray-700 hover:text-dropsugar-green hover:bg-gray-50 rounded-lg transition-all"
                title="Profile"
              >
                <User className="w-5 h-5" />
              </button>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all font-medium flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>

          {menuOpen && (
            <div className="md:hidden mt-4 space-y-2 pb-4">
              <button
                onClick={() => {
                  onNavigate('manage-package');
                  setMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-dropsugar-green/10 rounded-lg transition-all flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span>Manage Package</span>
              </button>
              <button
                onClick={() => {
                  setShowSettings(true);
                  setMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-all flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Settings</span>
              </button>
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center space-x-2"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <PersonalizedHeader
          onAddDevice={() => setShowAddDevice(true)}
        />

        {(subscription || isDummyData) && (
          <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 shadow-sm mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Initial Health State</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start space-x-4 p-4 bg-white/70 rounded-xl border-2 border-gray-200 hover:border-dropsugar-green transition-all">
                <div className="p-3 bg-dropsugar-green/10 rounded-xl">
                  <Droplets className="w-6 h-6 text-dropsugar-green" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Initial Glucose</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(sub as any)?.initialGlucoseReading ?? (sub as any)?.initial_glucose_reading ?? (isDummyData ? '98' : 'N/A')}{' '}
                    <span className="text-sm text-gray-600">mg/dL</span>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-4 bg-white/70 rounded-xl border-2 border-green-200 hover:border-green-400 transition-all">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Scale className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Initial Weight</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(sub as any)?.initialWeight ?? (sub as any)?.initial_weight ?? 'N/A'}{' '}
                    <span className="text-sm text-gray-600">kg</span>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-4 bg-white/70 rounded-xl border-2 border-red-200 hover:border-red-400 transition-all">
                <div className="p-3 bg-red-100 rounded-xl">
                  <Heart className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Initial BP</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(sub as any)?.initialBpSystolic && (sub as any)?.initialBpDiastolic
                      ? `${(sub as any).initialBpSystolic}/${(sub as any).initialBpDiastolic}`
                      : (sub as any)?.initial_bp_systolic && (sub as any)?.initial_bp_diastolic
                      ? `${(sub as any).initial_bp_systolic}/${(sub as any).initial_bp_diastolic}`
                      : (isDummyData ? '120/80' : 'N/A')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <GlucoseTrends />
          <div className="space-y-6">
            <MultiDayGlucoseChart />
            <LowBloodGlucoseIndex />
          </div>
        </div>

        {showQuickActions && (
          <>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Quick Actions</h3>
              <p className="text-gray-600">Access your health tools and resources</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <button
                onClick={() => onNavigate('steps-tracker')}
                className="group bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-dropsugar-green rounded-2xl p-8 transition-all text-left shadow-sm hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="p-3 bg-green-50 group-hover:bg-green-100 rounded-xl w-fit mb-4 transition-colors">
                      <Footprints className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">DropSugar Activity Tracker</h3>
                    <p className="text-gray-600">Track your daily activity and reach your goals</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <button
                onClick={() => onNavigate('calorie-tracker')}
                className="group bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-dropsugar-blue rounded-2xl p-8 transition-all text-left shadow-sm hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="p-3 bg-dropsugar-blue/10 group-hover:bg-dropsugar-blue/20 rounded-xl w-fit mb-4 transition-colors">
                      <Camera className="w-8 h-8 text-dropsugar-blue" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">DropSugar Plate Checker</h3>
                    <p className="text-gray-600">Snap a photo to track calories and nutrients</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-dropsugar-blue group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <button
                onClick={() => onNavigate('telehealth')}
                className="group bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-dropsugar-green rounded-2xl p-8 transition-all text-left shadow-sm hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="p-3 bg-teal-50 group-hover:bg-teal-100 rounded-xl w-fit mb-4 transition-colors">
                      <Video className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">DropSugar TeleHealth</h3>
                    <p className="text-gray-600">Book appointments with healthcare specialists</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                </div>
              </button>

              <button
                onClick={() => onNavigate('community-challenges')}
                className="group bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-dropsugar-green rounded-2xl p-8 transition-all text-left shadow-sm hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="p-3 bg-orange-50 group-hover:bg-orange-100 rounded-xl w-fit mb-4 transition-colors">
                      <Trophy className="w-8 h-8 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">DropSugar Challenges</h3>
                    <p className="text-gray-600">Compete with others and earn achievements</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            </div>
          </>
        )}

      </div>

      {showSettings && <UserSettings onClose={() => setShowSettings(false)} />}
      {showAddDevice && <AddDeviceModal onClose={() => setShowAddDevice(false)} />}
      {showTipPopup && displayTip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Tip of the Day</h2>
            <p className="text-gray-700 leading-relaxed mb-6">{displayTip}</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => {
                  window.localStorage.setItem('dropsugar:dashboard-tip-last-seen', displayTip);
                  setShowTipPopup(false);
                }}
                className="px-5 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              >
                Close
              </button>
              <button
                onClick={() => {
                  window.localStorage.setItem('dropsugar:dashboard-tip-disabled', 'true');
                  window.localStorage.setItem('dropsugar:dashboard-tip-last-seen', displayTip);
                  setTipPopupDisabled(true);
                  setShowTipPopup(false);
                }}
                className="px-5 py-3 rounded-xl bg-dropsugar-green text-white hover:bg-dropsugar-green-dark font-medium"
              >
                Disable tip popup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
